import {
	AfterViewChecked,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	viewChild,
	viewChildren,
} from '@angular/core';
import { getUrl, hasLink, isResource } from '@ngxp/rest';
import {
	AdressverwaltungFacade,
	KontaktInVerfahrenListResource,
	KontaktLinkRel,
	KontaktListLinkRel,
	KontaktListResource,
	KontaktResource,
	KontaktSearchMode,
	KontaktWithEditMode,
	SearchInputWithModes,
} from '@schir-int-client/adressverwaltung-shared';
import { clamp, debounce, isNil } from 'lodash-es';
import { Observable, Subject, Subscription } from 'rxjs';
import { AccessibilityService, ClipboardService } from '@schir-int-client/tech';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { KontaktAssignMenuComponent } from './kontakt-assign-menu/kontakt-assign-menu.component';
import { MailAddressService } from '../../../../../../tech/src/lib/mail-address/mail-address.service';
import { ApiRootFacade } from '@schir-int-client/api-root';
import { filter, take } from 'rxjs/operators';

@Component({
	selector: 'schir-int-client-adressverwaltung-kontakt-list',
	templateUrl: './adressverwaltung-kontakt-list.component.html',
	styleUrls: ['./adressverwaltung-kontakt-list.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class AdressverwaltungKontaktListComponent implements OnInit, AfterViewChecked, OnDestroy {
	@Input() selectedKontakt: KontaktResource;
	@Input() editMode: boolean;
	@Input() searchParameters: SearchInputWithModes;
	readonly kontakt_divs = viewChildren('kontaktList', { read: ElementRef });
	readonly scrollArea = viewChild<ElementRef>('scrollArea');

	@Output() setSelectedKontakt: EventEmitter<KontaktWithEditMode> = new EventEmitter();
	@Output() addKontakt: EventEmitter<void> = new EventEmitter();

	foundKontakte: Observable<KontaktListResource>;

	groupChar: string;
	showAddress: boolean = false;

	kontakte: KontaktResource[] = [];

	private subscriptions: Subscription[] = [];

	private numberItems: number;
	public audioAlert = new Subject<string>();
	allowWriteAccess: boolean;
	tooManyResults: boolean;
	protected readonly KontaktLinkRel = KontaktLinkRel;

	private scrollToSelected: boolean;

	constructor(
		public adressverwaltungFacade: AdressverwaltungFacade,
		private accessibilityService: AccessibilityService,
		private matDialog: MatDialog,
		private clipboardService: ClipboardService,
		private mailAddressService: MailAddressService,
		private apiRootFacade: ApiRootFacade,
		private changeDetection: ChangeDetectorRef
	) {}

	ngOnInit() {
		this.showAddress = this.apiRootFacade.isLFPR;
		this.foundKontakte = this.adressverwaltungFacade.kontakteBySearch;
		this.subscriptions.push(
			this.foundKontakte.subscribe((k: KontaktListResource) => {
				this.kontakte = k?._embedded?.kontaktList ?? [];
				this.allowWriteAccess = k && hasLink(k, KontaktListLinkRel.CREATE);
				this.tooManyResults = k?.page.totalPages > 1;
				this.changeDetection.markForCheck();
			})
		);
		this.subscriptions.push(
			this.adressverwaltungFacade.selectedKontaktActionRequested.subscribe(() => (this.scrollToSelected = true))
		);
	}

	ngAfterViewChecked() {
		const selectedLink = this.selectedKontakt?._links?.self.href;
		if (selectedLink) {
			const divElem = this.kontakt_divs().find(_item => {
				return _item.nativeElement.getAttribute('data-self-link') === selectedLink;
			});
			if (!isNil(divElem)) {
				this.scrollIntoViewIfNecessary(divElem);
				this.adressverwaltungFacade.rendered(divElem);
			}
		}
	}

	private scrollIntoViewIfNecessary(divElem: ElementRef) {
		if (this.scrollToSelected) {
			if (!this.isRenderedWithin(divElem.nativeElement, this.scrollArea().nativeElement)) {
				divElem.nativeElement.scrollIntoView();
			}
			this.scrollToSelected = false;
		}
	}

	ngOnDestroy() {
		this.subscriptions.forEach(s => s.unsubscribe());
	}

	private updateSearchParams = debounce(
		(phrase: string, filters: KontaktSearchMode[], limit: number) => {
			this.adressverwaltungFacade.setSearchParameters(phrase, filters.join(','), limit);
		},
		1500,
		{ leading: true }
	);

	doSearch(searchInputWithModes: SearchInputWithModes) {
		this.updateSearchParams(searchInputWithModes.searchString, searchInputWithModes.searchModes, 200);
	}

	doConfirmUpdate() {
		this.accessibilityService.shout(
			'Suchergebnisse aktualisiert: ' + this.numberItems + (this.numberItems == 1 ? 'Kontakt.' : ' Kontakte.'),
			this.audioAlert
		);
	}

	newGroupLetter(kontakt: KontaktResource): boolean {
		const char = kontakt.title.charAt(0).toLocaleUpperCase();
		if (char != this.groupChar) {
			this.groupChar = char;
			return true;
		} else {
			return false;
		}
	}

	patchKontaktAndFocus(kontakt: KontaktResource) {
		this.patchKontakt(kontakt);
		setTimeout(() => {
			if (kontakt.active) {
				this.accessibilityService.focusElementById('edit-button');
			} else {
				this.accessibilityService.focusElementById('activate-button');
			}
		});
	}

	patchKontakt(kontakt: KontaktResource) {
		this.scrollToSelected = true;
		this.setSelectedKontakt.emit(<KontaktWithEditMode>{ kontakt, editMode: this.editMode });
	}

	patchNewKontakt() {
		this.addKontakt.emit(null);
	}

	isNewKontakt(): boolean {
		return !isNil(this.selectedKontakt) && !isResource(this.selectedKontakt);
	}

	isSelectedKontakt(kontakt: KontaktResource) {
		if (this.selectedKontakt && this.selectedKontakt._links) {
			return kontakt._links.self.href == this.selectedKontakt._links.self.href;
		}
		return false;
	}

	copyMailAddressToClipboard(kontakt: KontaktResource) {
		const mailAddress = this.mailAddressService.createMailAddressForKontakt(kontakt);
		this.clipboardService.copyAndShowSnackBar(mailAddress, 'Adressdaten wurden in die Zwischenablage kopiert.');
	}

	openAssignMenu(kontakt: KontaktResource, kontaktDiv: HTMLElement, scrollArea: HTMLElement) {
		const assignWindowHeight = 500; // estimated value - will be moved after assign menu is rendered
		const y = this.getYPos(kontaktDiv) + kontaktDiv.offsetHeight - scrollArea.scrollTop - 20;

		this.adressverwaltungFacade.kontakteInVerfahren
			.pipe(
				filter(value => !isNil(value)),
				take(1)
			)
			.subscribe((kontakteInSelectedVerfahren: KontaktInVerfahrenListResource) => {
				const dialogConfig = new MatDialogConfig();
				dialogConfig.data = { kontakt, kontakteInSelectedVerfahren };
				dialogConfig.panelClass = 'kontaktAssignMenu';
				dialogConfig.autoFocus = false;
				dialogConfig.hasBackdrop = false;
				dialogConfig.position = { top: clamp(y, 10, window.innerHeight - assignWindowHeight - 10) + 'px' };
				this.matDialog.open(KontaktAssignMenuComponent, dialogConfig);
			});
	}

	private getYPos(element: HTMLElement) {
		let y = 0;
		while (element) {
			y += element.offsetTop + element.clientTop - element.scrollTop;
			element = <HTMLElement>element.offsetParent;
		}
		return y;
	}

	toggleKontaktDarstellung() {
		this.showAddress = !this.showAddress;
	}

	private isRenderedWithin(element: HTMLElement, parent: HTMLElement): boolean {
		const inner = element.getBoundingClientRect();
		const outer = parent.getBoundingClientRect();
		return (
			inner.top >= outer.top &&
			inner.left >= outer.left &&
			inner.bottom <= outer.bottom &&
			inner.right <= outer.right
		);
	}

	protected readonly getUrl = getUrl;
	protected readonly hasLink = hasLink;
}
